/**
 * The shape a finished turn's parts are stored in.
 *
 * `onFinish` used to replace the whole array with a single text part, which
 * discarded every step boundary and tool part the stream had built — the rail
 * appeared while streaming and vanished the instant the turn ended. That was
 * fixed by keeping the structural parts. What it still did was drop every
 * per-step TEXT part, and the rail labels its nodes from that text, so at the
 * moment a turn settled every label silently changed: the model's own narration
 * became a generic or a tool description.
 *
 * A label somebody read while the turn ran has to still say the same thing
 * afterwards. So the text parts stay, cleaned in place.
 *
 * The exception is a body that was genuinely REPLACED rather than cleaned — the
 * answer routed to the canvas and the chat shows a reference to it, or an
 * unfinished-turn notice was appended. There the per-step texts are no longer
 * the body, and keeping them would print the whole narration next to a link
 * claiming to stand in for it. Those collapse, as before.
 */

/**
 * Remove internal blocks the model was not supposed to emit, and truncate a
 * leaked tool result.
 *
 * A safety net rather than a parser: the leak check only fires after enough
 * clean content to be confident the tail is debris and not the answer.
 */
export function stripInternalBlocks(content: string): string {
  if (!content) return content
  let cleaned = content
    .replace(/<CRUCIBLE_TOOL_REQUEST>[\s\S]*?<\/CRUCIBLE_TOOL_REQUEST>/gi, '')
    .replace(/<CRUCIBLE_ACTION>[\s\S]*?<\/CRUCIBLE_ACTION>/gi, '')
    .replace(/<ODM_UPDATE>[\s\S]*?<\/ODM_UPDATE>/gi, '')
    .trim()

  // Detect leaked tool results: if content contains literal \n escape sequences
  // (not actual newlines), it means a JSON string value from a tool result
  // was concatenated into the response. Truncate at the leak boundary.
  const leakIndex = cleaned.indexOf('\\n')
  if (leakIndex > 0) {
    // Only truncate if the leak is after substantial clean content
    const beforeLeak = cleaned.substring(0, leakIndex).trim()
    if (beforeLeak.length > 100) {
      cleaned = beforeLeak
        .replace(/["']\s*\}\s*\}\s*$/, '')
        .trim()
    }
  }

  return cleaned
}


export interface TurnPart {
  type: string
  text?: string
  [key: string]: unknown
}

const textPart = (text: string): TurnPart => ({ type: 'text', text })

/**
 * The parts to store for a settled turn.
 *
 * `bodyRewritten` says which of the two cases applies: false when
 * `displayContent` is just the cleaned text, true when it replaced or extended
 * it. The caller knows; this cannot infer it, and guessing by comparing strings
 * would break the moment cleaning became a no-op.
 */
export function finalizeTurnParts<T extends TurnPart>(
  parts: unknown,
  displayContent: string,
  bodyRewritten: boolean,
): T[] {
  // The one cast in here. Callers hold the SDK's discriminated part union,
  // which is narrower than anything this can express while also SYNTHESISING a
  // text part — so the element type is preserved for the caller and the cast is
  // paid once, here, rather than at every call site.
  const synthesised = (text: string) => textPart(text) as T

  if (!Array.isArray(parts)) return [synthesised(displayContent)]

  const valid = parts.filter(
    (p): p is T =>
      Boolean(p) && typeof p === 'object' && typeof (p as TurnPart).type === 'string',
  )

  if (bodyRewritten) {
    return [...valid.filter((p) => p.type !== 'text'), synthesised(displayContent)]
  }

  const kept: T[] = []
  let sawText = false
  for (const part of valid) {
    if (part.type !== 'text') {
      kept.push(part)
      continue
    }
    const cleaned = stripInternalBlocks(typeof part.text === 'string' ? part.text : '')
    // An emptied text part renders as a blank markdown block, so drop it.
    if (!cleaned.trim()) continue
    kept.push({ ...part, text: cleaned })
    sawText = true
  }

  // Something has to render. A turn whose text cleaned away to nothing still
  // needs a body, and the caller's displayContent is the honest fallback.
  if (!sawText) kept.push(synthesised(displayContent))
  return kept
}
